import React, { useCallback, useEffect, useState } from 'react';
import { memo } from 'react';
import toast from 'react-hot-toast';
import { useUsersStore } from '../../store/useUsersStore';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthContext';
import SimpleAddTemplateForm from './SimpleAddTemplateForm';
import TemplatesListTable from './TemplatesListTable';

const AITemplatesPage = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const { user: fbUser } = useFirebaseAuth();

  const getSelectedUser = useUsersStore((s) => s.getSelectedUser);
  const selectedSubUser = getSelectedUser ? getSelectedUser() : null;
  const selectedSubUserId = selectedSubUser ? (selectedSubUser.document_id || selectedSubUser.sub_user_id || selectedSubUser.id) : null;

  const loadAiTemplates = useUsersStore((s) => s.loadAiTemplates);
  const fetchUsers = useUsersStore((s) => s.fetchUsers);
  const selectTemplate = useUsersStore((s) => s.selectTemplate);

  // Single load on mount / sub-user change. Stable deps to avoid repeated API calls; idToken read inside effect.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (selectedSubUserId) {
          const loadAiTemplatesFn = useUsersStore.getState().loadAiTemplates;
          if (typeof loadAiTemplatesFn === 'function') {
            const idToken = typeof fbUser?.getIdToken === 'function' ? await fbUser.getIdToken() : null;
            if (!cancelled) await loadAiTemplatesFn(selectedSubUserId, idToken);
          }
        } else {
          const fetchUsersFn = useUsersStore.getState().fetchUsers;
          const parentUid = useUsersStore.getState().parentUid;
          if (typeof fetchUsersFn === 'function' && parentUid && !cancelled) await fetchUsersFn(parentUid);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load AI templates', err);
          toast.error('Failed to load AI templates');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSubUserId]); // fbUser intentionally omitted to avoid re-run on auth object reference change

  const openForm = useCallback(() => {
    setEditingTemplate(null);
    setShowForm(true);
  }, []);

  const closeForm = useCallback(() => {
    setShowForm(false);
    setEditingTemplate(null);
  }, []);

  const handleAddSuccess = useCallback((saved) => {
    setShowForm(false);
    setEditingTemplate(null);
    if (typeof loadAiTemplates === 'function' && selectedSubUserId) {
      (async () => {
        try {
          const idToken = typeof fbUser?.getIdToken === 'function' ? await fbUser.getIdToken() : null;
          await loadAiTemplates(selectedSubUserId, idToken);
        } catch {
          // already shown in form
        }
      })();
    }
  }, [loadAiTemplates, selectedSubUserId, fbUser]);

  const handleEdit = useCallback((tpl) => {
    try { if (typeof selectTemplate === 'function') selectTemplate(tpl); } catch (e) { console.warn(e); }
    setEditingTemplate(tpl);
    setShowForm(true);
  }, [selectTemplate]);

  const handleUse = useCallback((tpl) => {
    const selectTemplateFn = useUsersStore.getState().selectTemplate;
    if (typeof selectTemplateFn === 'function') selectTemplateFn(tpl);
    toast.success('Template selected');
  }, []);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setShowForm(false); };
    if (showForm) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showForm]);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded shadow p-6">
          {/* Header Section - Same as CategoriesTable */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-900">AI Templates</h2>
            <button 
              onClick={openForm} 
              className="px-5 py-2.5 bg-white border-[1.5px] border-primary-blue text-primary-blue font-medium text-sm rounded-lg hover:bg-primary-blue hover:text-white transition-colors"
            >
              Add AI Template
            </button>
          </div>

          {/* Table - show only when modal is not open */}
          {!showForm && (
            <TemplatesListTable onEdit={handleEdit} onUse={handleUse} />
          )}
        </div>
      </div>

      {/* Modal overlay */}
     {showForm && (
  <div
    role="dialog"
    aria-modal="true"
    className="fixed inset-0 z-50 flex items-center justify-center p-4"
  >
    {/* Backdrop with white blur - matching Figma */}
    <div 
      className="fixed inset-0 bg-white/60 backdrop-blur-sm" 
      onClick={closeForm} 
    />

    {/* Centered modal content */}
    <div className="relative z-10 w-full max-w-md">
      <SimpleAddTemplateForm 
        selectedTemplate={editingTemplate} 
        onSuccess={handleAddSuccess} 
        onCancel={closeForm} 
      />
    </div>
  </div>
)}

    </div>
  );
};

export default memo(AITemplatesPage);
